/**
 * 平台超管登录态 store（add-platform-admin-web）。
 *
 * 与员工 auth store 完全独立：token 持久化到 localStorage `platform-token`
 * （utils/platform-session.ts），身份为平台超管（无租户/无角色体系）。
 * 页面刷新 → 从 localStorage 恢复 token，身份在路由守卫中调 platform-me 重新拉取。
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { PlatformAdminAccount } from '@cms/shared'
import { clearPlatformToken, getPlatformToken, setPlatformToken } from '@/utils/platform-session'

/** 平台超管身份视图（登录/me 返回仅 id + username）。 */
type PlatformAdminView = Pick<PlatformAdminAccount, 'id' | 'username'> & {
  displayName?: string
  roleCode?: string
  permissions?: string[]
  dataScope?: 'SELF' | 'TEAM' | 'ALL'
  teamId?: string | null
}

export const usePlatformAuthStore = defineStore('platformAuth', () => {
  const token = ref<string | null>(getPlatformToken())
  const admin = ref<PlatformAdminView | null>(null)
  const mustChangePassword = ref(false)

  const permissions = computed(() => admin.value?.permissions ?? [])
  const dataScope = computed(() => admin.value?.dataScope ?? 'SELF')

  const isLoggedIn = computed(() => !!token.value)
  /** 是否存在超管会话（守卫互斥判定用）。 */
  const hasAdmin = computed(() => !!token.value)

  function setLogin(payload: {
    token: string
    admin: PlatformAdminView
    mustChangePassword: boolean
  }): void {
    token.value = payload.token
    setPlatformToken(payload.token)
    admin.value = payload.admin
    mustChangePassword.value = payload.mustChangePassword
  }

  /** 从 platform-me 恢复完整身份（刷新页面后）。 */
  function setIdentity(me: { admin: PlatformAdminView; mustChangePassword: boolean }): void {
    admin.value = me.admin
    mustChangePassword.value = me.mustChangePassword
  }

  function logout(): void {
    token.value = null
    clearPlatformToken()
    admin.value = null
    mustChangePassword.value = false
  }

  function setMustChangePassword(value: boolean): void {
    mustChangePassword.value = value
  }

  /** 平台权限仅控制界面呈现；服务端仍是最终授权边界。 */
  function hasPermission(permission: string): boolean {
    // 兼容迁移窗口内旧 platform-me 身份；新服务端身份始终返回 permissions。
    if (admin.value && admin.value.permissions === undefined) return true
    return permissions.value.includes('*') || permissions.value.includes(permission)
  }

  return {
    token,
    admin,
    mustChangePassword,
    isLoggedIn,
    hasAdmin,
    permissions,
    dataScope,
    hasPermission,
    setLogin,
    setIdentity,
    setMustChangePassword,
    logout,
  }
})
